import { useCallback } from "react";
import Label from "../label/Label";
import { cn } from "@/lib/styles.utils";
import { Icon } from "../icons/Icon";

type Size = "large" | "medium";

interface InputTextareaProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (snData: string) => void;
  size: Size;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  warning?: boolean;
  error?: boolean;
  success?: boolean;
  helpText?: string;
  name: string;
  width?: string;
}

const InputTextarea = ({
  label,
  placeholder,
  value,
  size,
  onChange,
  disabled,
  readOnly,
  required,
  warning,
  error,
  success,
  helpText,
  name,
  width,
}: InputTextareaProps) => {
  const id = name;
  const requiredAlert = Boolean(required && value.trim().length === 0);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  const borderState =
    error || requiredAlert
      ? "border-ib-red-main"
      : warning
        ? "border-ib-orange-warning"
        : success
          ? "border-ib-green-succes"
          : "border-ib-gray-borders";

  return (
    <div style={{ width: width ?? "100%" }}>
      <div className="mb-2 flex items-center gap-x-2">
        <Label label={label} id={id} required={required} />
      </div>
      <textarea
        id={id}
        name={id}
        rows={3}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        className={cn(
          "w-full rounded-[2px] border bg-ib-ib-white text-sm leading-[20px]",
          "read-only:bg-ib-gray-lightest read-only:text-ib-gray-darkest",
          "disabled:border-ib-gray-borders read-only:border-ib-gray-borders",
          "resize-none",
          size === "medium" ? "px-3 py-[9px]" : "px-3 py-[13px]",
          borderState,
          !requiredAlert &&
            !readOnly &&
            "hover:border-ib-red-lightest-2 focus:border-ib-red-main",
          "focus:outline-none",
          disabled
            ? "placeholder:text-ib-gray-dark-2"
            : "placeholder:ib-gray-disabled",
        )}
      />

      {helpText && (
        <div className="flex gap-2 items-center">
          <Icon name="info" className="size-4 text-ib-gray-dark-2" />
          <p className="text-sm text-ib-gray-dark-2">{helpText}</p>
        </div>
      )}
    </div>
  );
};

export default InputTextarea;
